import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import axios from 'axios';
import { API } from '../config/api';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Verificando pago...');
  const checkCount = useRef(0);
  const maxChecks = 10;

  useEffect(() => {
    // MercadoPago puede enviar varios parámetros
    const externalRef = searchParams.get('external_ref');
    const collectionStatus = searchParams.get('collection_status');
    const mpStatus = searchParams.get('status');
    const paymentId = searchParams.get('payment_id') || searchParams.get('collection_id');

    // Si MercadoPago dice que está aprobado directamente
    if (collectionStatus === 'approved' || mpStatus === 'approved') {
      // Aún así verificar con el backend para activar PRO
      if (externalRef) {
        verifyAndActivate(externalRef);
      } else {
        setStatus('success');
        setMessage('¡Pago aprobado! Activando tu cuenta PRO...');
        // Verificar con backend para activar
        if (paymentId) {
          verifyByPaymentId(paymentId);
        } else if (externalRef) {
          verifyAndActivate(externalRef);
        } else {
          setTimeout(() => navigate('/app'), 2000);
        }
      }
      return;
    }

    // Si tenemos payment_id, verificar directamente
    if (paymentId && (collectionStatus === 'approved' || mpStatus === 'approved')) {
      verifyByPaymentId(paymentId);
    } else if (externalRef) {
      verifyAndActivate(externalRef);
    } else if (collectionStatus === 'pending' || mpStatus === 'pending' || mpStatus === 'in_process') {
      setStatus('loading');
      setMessage('Tu pago está siendo procesado. Esto puede tomar unos minutos.');
    } else if (collectionStatus === 'rejected' || mpStatus === 'rejected') {
      setStatus('error');
      setMessage('El pago fue rechazado. Por favor intenta con otro medio de pago.');
    } else {
      setStatus('error');
      setMessage('No se encontró información del pago');
    }
  }, [searchParams]);

  const verifyByPaymentId = async (paymentId) => {
    try {
      const response = await axios.get(`${API}/payments/verify/${paymentId}`, {
        withCredentials: true
      });
      
      if (response.data.payment_status === 'approved') {
        setStatus('success');
        setMessage('¡Bienvenido a DATAPRO PRO!');
        setTimeout(() => navigate('/app'), 2000);
      } else {
        setStatus('error');
        setMessage(`Estado del pago: ${response.data.payment_status}`);
      }
    } catch (error) {
      console.error('Error verifying by payment_id:', error);
      setStatus('error');
      setMessage('Error al verificar. Contacta a soporte.');
    }
  };

  const verifyAndActivate = async (externalRef) => {
    checkCount.current += 1;
    
    try {
      const response = await axios.get(`${API}/payments/status/${externalRef}`, {
        withCredentials: true
      });

      if (response.data.payment_status === 'approved' || response.data.user_plan === 'pro') {
        setStatus('success');
        setMessage(response.data.message || '¡Bienvenido a DATAPRO PRO!');
        // Esperar un momento y redirigir
        setTimeout(() => navigate('/app'), 2000);
      } else if (response.data.payment_status === 'pending' || response.data.payment_status === 'in_process') {
        setMessage('Verificando pago con MercadoPago...');
        // Reintentar si no hemos llegado al límite
        if (checkCount.current < maxChecks) {
          setTimeout(() => verifyAndActivate(externalRef), 3000);
        } else {
          setStatus('loading');
          setMessage('El pago está siendo procesado. Puede tomar unos minutos. Revisa tu email.');
        }
      } else if (response.data.payment_status === 'rejected') {
        setStatus('error');
        setMessage('El pago fue rechazado');
      } else {
        // Estado desconocido, seguir esperando
        if (checkCount.current < maxChecks) {
          setTimeout(() => verifyAndActivate(externalRef), 3000);
        }
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      if (checkCount.current < maxChecks) {
        setTimeout(() => verifyAndActivate(externalRef), 3000);
      } else {
        setStatus('error');
        setMessage('Error al verificar el pago. Contacta a soporte.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6">
      <Card className="w-full max-w-md bg-neutral-900 border-red-900/30">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-red-500 mx-auto mb-4 animate-spin" />
              <h2 className="text-2xl font-bold text-white mb-2">Verificando pago...</h2>
              <p className="text-gray-400">Por favor espera un momento</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">¡Pago Exitoso!</h2>
              <p className="text-gray-400 mb-6">{message}</p>
              <div className="space-y-3">
                <Link to="/app">
                  <Button className="w-full bg-red-600 hover:bg-red-700">
                    Ir a la Aplicación
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Error en el Pago</h2>
              <p className="text-gray-400 mb-6">{message}</p>
              <div className="space-y-3">
                <Link to="/pricing">
                  <Button className="w-full bg-red-600 hover:bg-red-700">
                    Intentar de Nuevo
                  </Button>
                </Link>
                <Link to="/app">
                  <Button variant="outline" className="w-full">
                    Volver a la App
                  </Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
